import {
  CURSOR_ALLOWANCE_PX,
  TOPIC_RAIL_GUTTER_PX,
} from '@src/components/transcript/topicRailStyles';
import { useEffect, useState } from 'react';

import type React from 'react';

/**
 * The rail itself is 2–3px and the gutter it sits in is only 8px wide (the
 * transcript container starts at the pane's own left edge), so the hit band is
 * padded on both sides to be pointable at all.
 *
 * The leftward padding runs PAST the pane's edge, into the chrome beside it —
 * which is why the listener sits on `document` rather than the pane. Approach a
 * rail from the left and it lights up before the pointer has even reached the
 * transcript. Rightward padding reaches into the paragraph's leading edge; the
 * tooltip never opens over the line under the pointer, so that costs nothing.
 */
const RAIL_HIT_PADDING_LEFT_PX = 28;
const RAIL_HIT_PADDING_RIGHT_PX = 10;

/**
 * Cheap gate before any layout read: the rails all sit in the pane's left
 * gutter, so a pointer anywhere else bails without measuring a single
 * paragraph. Generous enough to cover the gutter plus whatever inset the
 * transcript container carries.
 */
const RAIL_SCAN_WIDTH_PX = 60;

export interface TopicRailHover {
  paragraphId: string;
  /** Selected topics this paragraph matches; the first owns the rail color. */
  titles: string[];
  /** Viewport x of the rail's left edge. */
  x: number;
  /**
   * Viewport y of the rail's TOP (clamped into the pane), not the pointer's —
   * the tooltip opens upward from there, so it clears the whole marked
   * paragraph instead of landing on the line being pointed at.
   */
  y: number;
}

/**
 * Hover state for the topic rails in the transcript's left gutter (PLAT-11039
 * rails, PLAT-11182 colors) — the host renders the tooltip that names the
 * topic.
 *
 * The rail is a `::before` pseudo-element painted OUTSIDE the paragraph's box
 * (see `topicRailStyles`), so nothing can carry a tooltip anchor and `:hover`
 * on the paragraph would fire over its text too. Instead this delegates a single
 * `pointermove` listener and hit-tests the gutter band around each
 * rail-bearing paragraph, which works for both rail paths — the imperative DOM
 * classes (`adjustDOMClassesToTopicHighlight`) and the translated views'
 * React-rendered `topicRailProps`, since both land `.topic-highlighted` on the
 * `[data-paragraph-id]` element.
 *
 * State only changes when the hovered paragraph changes, so moving along a rail
 * doesn't re-render the tab.
 */
export function useTopicRailHover(
  paneRef: React.RefObject<HTMLElement | null>,
  paragraphTopicTitles: ReadonlyMap<string, string[]>,
): TopicRailHover | null {
  const [hover, setHover] = useState<TopicRailHover | null>(null);

  useEffect(() => {
    const pane = paneRef.current;
    // No topics selected means no rails to hover: skip the listeners entirely.
    if (!pane || paragraphTopicTitles.size === 0) {
      setHover(null);
      return;
    }

    const clear = () => setHover(prev => (prev ? null : prev));

    const handleMove = (event: PointerEvent) => {
      const { clientX: x, clientY: y } = event;
      const paneRect = pane.getBoundingClientRect();
      if (
        x > paneRect.left + RAIL_SCAN_WIDTH_PX ||
        x < paneRect.left - RAIL_HIT_PADDING_LEFT_PX ||
        y < paneRect.top ||
        y > paneRect.bottom
      ) {
        clear();
        return;
      }

      const paragraphs = pane.querySelectorAll<HTMLElement>(
        '[data-paragraph-id].topic-highlighted',
      );
      for (let i = 0; i < paragraphs.length; i++) {
        const paragraph = paragraphs[i];
        const rect = paragraph.getBoundingClientRect();
        if (y < rect.top || y > rect.bottom) continue;
        const railLeft =
          rect.left - (TOPIC_RAIL_GUTTER_PX + CURSOR_ALLOWANCE_PX);
        if (
          x < railLeft - RAIL_HIT_PADDING_LEFT_PX ||
          x > rect.left + RAIL_HIT_PADDING_RIGHT_PX
        ) {
          continue;
        }
        const paragraphId = paragraph.getAttribute('data-paragraph-id');
        const titles = paragraphId
          ? paragraphTopicTitles.get(paragraphId)
          : undefined;
        if (!paragraphId || !titles?.length) continue;
        setHover(prev =>
          prev?.paragraphId === paragraphId
            ? prev
            : {
                paragraphId,
                titles,
                x: railLeft,
                y: Math.max(rect.top, paneRect.top),
              },
        );
        return;
      }
      clear();
    };

    // On `document`, not the pane: the band reaches left of the pane's edge, so
    // the pane's own pointer events would miss exactly the approach this is
    // meant to make easy. The x/y gate above bails on one rect read for every
    // pointer position that isn't in the gutter.
    document.addEventListener('pointermove', handleMove);
    // Capture: the scroll happens on a descendant (the transcript's scroll
    // container), and a scrolled-away rail leaves the tooltip stranded.
    pane.addEventListener('scroll', clear, true);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      pane.removeEventListener('scroll', clear, true);
    };
  }, [paneRef, paragraphTopicTitles]);

  return hover;
}
